import i18next from 'i18next';
import { all, put, call, takeEvery, select } from 'redux-saga/effects';
import { PapiStudioPresetInit, PapiStudioPresetUpdate } from '@wix/da-papi-types';
import { isApiError, requestPuppy, requestNapi } from '@wix/da-http-client';
import { BiData, StudioSavePresetsBiEvent } from '@wix/da-bi/pkg/events';
import { logBiEvent } from '@wix/da-shared-react/pkg/biLogger/redux/saga';
import { popModal } from '@wix/da-shared-react/pkg/Modals/redux/actionCreators';
import { addErrorNotification } from '@wix/da-shared-react/pkg/Notifications/redux/actionCreators';
import { NotificationDuration } from '@wix/da-shared-react/pkg/Notifications/Notification';
import { fieldToLicense } from '../../fields/LicenseField/adapters';
import {
  presetsInitFetch,
  presetsInitSuccess,
  presetsInitError,
  presetsSave,
  presetsSaveSuccess,
  presetsSetActive,
  presetsCreate,
  presetsDelete,
  presetsPromptDismiss,
} from '../actions';
import { getPresetsInitResponse } from '../selectors';
import { PresetsInitResponse } from '../types';

function* handlePresetsInitFetch({ payload: { presetId } }) {
  const result: PapiStudioPresetInit = yield call(
    requestPuppy,
    {
      method: 'post',
      url: 'preset/init',
      data: {
        presetid: presetId !== null ? (presetId ?? 0) : undefined,
      },
    },
    undefined,
    'studio'
  );

  if (!result || isApiError(result)) {
    yield put(popModal());
    yield put(addErrorNotification(i18next.t('studio.presets.init.error'), NotificationDuration.S));
    return;
  }

  if (result) {
    const selectedLicenseValue = field => field.filter(f => f.selected)[0]?.value;

    const initSuccessResponse = {
      deviation: {
        deviationId: 0,
        typeId: 0,
        canUpdateAiClaim: true,
        title: result.title,
        isDownloadable: result.allowFreeDownload,
        isCommentable: result.allowComments,
        isAiGenerated: result.isAiGenerated,
        isAiUseDisallowed: result.isAiUseDisallowed,
        isMature: result.isMature,
        license: fieldToLicense({
          cc: selectedLicenseValue(result.license.creativeCommons),
          attr: selectedLicenseValue(result.license.commercial),
          deriv: selectedLicenseValue(result.license.modify),
        }),
        extended: {
          subjectTags: result.subjectTags,
          locationTag: result.locationTag,
          descriptionText: result.description,
          hasWatermark: result.addWatermark,
        },
      },
      description: result.description,
      tags: (result.tags || []).map(tag => ({ ...tag, name: tag.tag })),
      published: false,
      license: result.license,
      displayOptions: result.displayOptions,
      displayIndex: result.displayResolution,
      presets: [],
    };

    const galleries = yield select(state => state.studio!.galleries);
    const initSuccessResponseWithPresets: PresetsInitResponse = {
      ...initSuccessResponse,
      presetId: (result as PapiStudioPresetInit).presetId!,
      presetName: (result as PapiStudioPresetInit).presetName!,
      isActive: (result as PapiStudioPresetInit).isActive!,
      presets: (result as PapiStudioPresetInit).presets!,
      galleries,
      inGalleries: (result as PapiStudioPresetInit).galleries || [],
      disableAutotags: result.disableAutotags,
    };
    yield put(presetsInitSuccess(initSuccessResponseWithPresets));
  } else {
    yield put(presetsInitError());
  }
}

function* handlePresetsSave({
  payload: { data, dismissPrompt, nextPreset },
}: ReturnType<typeof presetsSave>) {
  const { descriptionState, ...rest } = data;

  const editorRaw = descriptionState;

  const response: PapiStudioPresetUpdate = yield call(requestNapi, {
    method: 'post',
    url: '/studio/preset/update',
    data: { editorRaw, ...rest },
  });
  if (!response || isApiError(response)) {
    yield put(
      addErrorNotification(i18next.t('studio.presets.update.error'), NotificationDuration.S)
    );
    return;
  }
  yield put(presetsSaveSuccess());

  if (dismissPrompt) {
    yield put(presetsPromptDismiss());
    yield put(presetsInitFetch(nextPreset));
  } else {
    yield put(popModal());
  }
}

function* handlePresetsSetActive({ payload: { presetId } }: ReturnType<typeof presetsSetActive>) {
  const response: PapiStudioPresetUpdate = yield call(requestNapi, {
    method: 'post',
    url: '/studio/preset/update',
    data: {
      presetid: presetId,
      is_active: true,
    },
  });
  if (!response || isApiError(response)) {
    yield put(
      addErrorNotification(i18next.t('studio.presets.update.error'), NotificationDuration.S)
    );
    return;
  }

  yield put(presetsInitFetch(null));
}

function* handlePresetsCreate({ payload: { data } }: ReturnType<typeof presetsCreate>) {
  // If this is a dupe operation, save the preset we're duplicating
  // We could've yield*'d to the save handler, but our build
  // output JS doesn't go as high as supporting that...
  const { descriptionState, presetid, ...rest } = data;
  if (typeof presetid !== 'undefined') {
    const editorRaw = descriptionState;

    const response: PapiStudioPresetUpdate = yield call(requestNapi, {
      method: 'post',
      url: '/studio/preset/update',
      data: { editorRaw, presetid, ...rest },
    });
    if (!response || isApiError(response)) {
      yield put(
        addErrorNotification(i18next.t('studio.presets.update.error'), NotificationDuration.S)
      );
      return;
    }
  }

  const createResponse = yield call(
    requestPuppy,
    {
      method: 'post',
      url: 'preset/create',
      data: { presetid },
    },
    undefined,
    'studio'
  );

  if (!createResponse || isApiError(createResponse)) {
    yield put(
      addErrorNotification(i18next.t('studio.presets.create.error'), NotificationDuration.S)
    );
  } else {
    const initResponse = yield select(getPresetsInitResponse);
    yield call(
      logBiEvent,
      BiData<StudioSavePresetsBiEvent>({
        evid: 13,
        presetid: createResponse['presetId'],
        previous_presetid: initResponse.presetId,
        is_preset_created: 1,
      })
    );
    yield put(presetsInitFetch(null));
  }
}

function* handlePresetsDelete({ payload: { presetId } }: ReturnType<typeof presetsDelete>) {
  const response = yield call(
    requestPuppy,
    {
      method: 'post',
      url: 'preset/delete',
      data: { presetid: presetId },
    },
    undefined,
    'studio'
  );

  yield put(presetsPromptDismiss());
  if (!response || isApiError(response)) {
    yield put(
      addErrorNotification(i18next.t('studio.presets.delete.error'), NotificationDuration.S)
    );
  } else {
    yield put(presetsInitFetch(null));
  }
}

export default function* presetsSaga() {
  yield all([
    takeEvery(presetsInitFetch, handlePresetsInitFetch),
    takeEvery(presetsSave, handlePresetsSave),
    takeEvery(presetsSetActive, handlePresetsSetActive),
    takeEvery(presetsCreate, handlePresetsCreate),
    takeEvery(presetsDelete, handlePresetsDelete),
  ]);
}
